import React, { forwardRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Label,
  NumericTextField,
  TextField,
  useLocalization,
} from '@ui-framework/index';

import IconLibrary from '../IconLibrary';

function getHeaders(formatMessage, unUsedFields) {
  return {
    action: {
      key: 'action',
      label: '',
      required: false,
    },
    partLineCode: {
      key: 'partLineCode',
      label: formatMessage({
        id: 'LINE_CODE',
        defaultMessage: 'Line Code',
      }),
      required: false,
    },
    partNumber: {
      key: 'partNumber',
      label: formatMessage({
        id: 'PART_NUMBER',
        defaultMessage: 'Part Number',
      }),
      required: true,
    },
    partDescription: {
      key: 'partDescription',
      label: formatMessage({
        id: 'DESCRIPTION',
        defaultMessage: 'Description',
      }),
      required: false,
    },
    partQuantity: {
      key: 'partQuantity',
      label: formatMessage({
        id: 'QUANTITY',
        defaultMessage: 'Quantity',
      }),
      required: true,
    },
    partUnitCost: {
      key: 'partUnitCost',
      label: formatMessage({
        id: 'UNIT_COST',
        defaultMessage: 'Unit Cost',
      }),
      required: true,
    },
    ...(!unUsedFields.includes('partHandling')
      ? {
          partHandling: {
            key: 'partHandling',
            label: formatMessage({
              id: 'HANDLING',
              defaultMessage: 'Handling',
            }),
            required: false,
          },
        }
      : {}),
    ...(!unUsedFields.includes('partTax')
      ? {
          partTax: {
            key: 'partTax',
            label: formatMessage({
              id: 'TAX',
              defaultMessage: 'Tax',
            }),
            required: false,
          },
        }
      : {}),
    total: {
      key: 'total',
      label: formatMessage({
        id: 'TOTAL',
        defaultMessage: 'Total',
      }),
      required: false,
    },
  };
}

const emptyField = {
  partLineCode: '',
  partNumber: '',
  description: '',
  partQuantity: '',
  partUnitCost: '',
  partHandling: '',
  partTax: '',
};

function PartLabel({ idx, children, ...props }) {
  if (idx > 0) {
    return null;
  }
  return <Label {...props}>{children}</Label>;
}

const Parts = forwardRef((props, ref) => {
  const { formatMessage } = useLocalization();
  const { control, watch } = useFormContext();
  const [currentRows, setCurrentRows] = useState([]);

  const unUsedFields = props?.field?.unusedFields || [];
  const maxFields = props?.field?.max || 100;
  const headers = getHeaders(formatMessage, unUsedFields);

  const { fields, append, remove } = useFieldArray({
    control,
    name: props?.field?.name,
  });

  const parts = watch(props?.field?.name || 'undefined');

  useEffect(() => {
    if (Array.isArray(parts) && parts.length > 0) {
      setCurrentRows([...parts]);
    }
  }, [parts]);

  return (
    <Box
      ref={ref}
      sx={{
        p: 1,
      }}>
      {fields.map((field, idx) => {
        const { partQuantity, partUnitCost, partHandling, partTax } =
          currentRows[idx] || {
            partQuantity: 0,
            partUnitCost: 0,
            partHandling: 0,
            partTax: 0,
          };
        const q = parseFloat(partQuantity) || 0;
        const u = parseFloat(partUnitCost) || 0;
        const h = parseFloat(partHandling) || 0;
        const t = parseFloat(partTax) || 0;
        const total = parseFloat(q * u + h + t).toFixed(2) || 0;

        return (
          <Grid2
            container
            spacing={1}
            alignItems="center"
            key={field.id}
            sx={{ mb: 1 }}>
            <Grid2 xs={0.5}>
              <PartLabel
                idx={idx}
                component="div"
                style={{ display: 'block', marginBottom: '5px' }}>
                &nbsp;
              </PartLabel>
              <IconButton
                size="small"
                onClick={() => {
                  remove(idx);
                }}
                color="error">
                <DeleteIcon />
              </IconButton>
            </Grid2>
            <Grid2 xs={12} md={1}>
              <PartLabel
                idx={idx}
                component="div"
                style={{ display: 'block', marginBottom: '5px' }}
                required={headers?.partLineCode?.required}>
                {headers.partLineCode?.label}
              </PartLabel>
              <Controller
                control={control}
                name={`${props.field.name}.${idx}.partLineCode`}
                render={({ field: controllerField }) => (
                  <Label component="div" sx={{ m: 1 }}>
                    {controllerField?.value}
                  </Label>
                )}
              />
            </Grid2>
            <Grid2 xs={12} md={2}>
              <PartLabel
                idx={idx}
                component="div"
                style={{ display: 'block', marginBottom: '5px' }}
                required={headers?.partNumber?.required}>
                {headers.partNumber?.label}
              </PartLabel>
              <Controller
                control={control}
                name={`${props.field.name}.${idx}.partNumber`}
                render={({
                  field: controllerField,
                  fieldState: { error: fsError },
                }) => (
                  <TextField
                    {...controllerField}
                    error={fsError?.message || false}
                    helperText={fsError?.message}
                    value={
                      controllerField.value === undefined
                        ? ''
                        : controllerField.value
                    }
                    permission={props?.permission?.partNumber || 'READ'}
                  />
                )}
              />
            </Grid2>
            <Grid2 xs={12} md={2}>
              <PartLabel
                idx={idx}
                component="div"
                style={{ display: 'block', marginBottom: '5px' }}
                required={headers?.partDescription?.required}>
                {headers.partDescription?.label}
              </PartLabel>
              <Controller
                control={control}
                name={`${props.field.name}.${idx}.partDescription`}
                render={({ field: controllerField }) => (
                  <TextField
                    {...controllerField}
                    value={
                      controllerField.value === undefined
                        ? ''
                        : controllerField.value
                    }
                    permission={props?.permission?.partDescription || 'READ'}
                  />
                )}
              />
            </Grid2>
            <Grid2 xs={12} md={1}>
              <PartLabel
                idx={idx}
                component="div"
                style={{ display: 'block', marginBottom: '5px' }}
                required={headers?.partQuantity?.required}>
                {headers.partQuantity?.label}
              </PartLabel>
              <Controller
                control={control}
                name={`${props.field.name}.${idx}.partQuantity`}
                render={({
                  field: controllerField,
                  fieldState: { error: fsError },
                }) => (
                  <TextField
                    {...controllerField}
                    error={fsError?.message || false}
                    helperText={fsError?.message}
                    value={
                      controllerField.value === undefined
                        ? ''
                        : controllerField.value
                    }
                    permission={props?.permission?.partQuantity || 'READ'}
                  />
                )}
              />
            </Grid2>
            <Grid2 xs={12} md={1}>
              <PartLabel
                idx={idx}
                component="div"
                style={{ display: 'block', marginBottom: '5px' }}
                required={headers?.partUnitCost?.required}>
                {headers.partUnitCost?.label}
              </PartLabel>
              <NumericTextField
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                startAdornment="$"
                name={`${props.field.name}.${idx}.partUnitCost`}
                permission={props?.permission?.partUnitCost || 'READ'}
              />
            </Grid2>
            {!unUsedFields.includes('partHandling') && (
              <Grid2 xs={12} md={1}>
                <PartLabel
                  idx={idx}
                  component="div"
                  style={{ display: 'block', marginBottom: '5px' }}
                  required={headers?.partHandling?.required}>
                  {headers.partHandling?.label}
                </PartLabel>
                <NumericTextField
                  decimalScale={2}
                  fixedDecimalScale
                  startAdornment="$"
                  allowNegative={false}
                  name={`${props.field.name}.${idx}.partHandling`}
                  permission={props?.permission?.partHandling || 'READ'}
                />
              </Grid2>
            )}
            {!unUsedFields.includes('partTax') && (
              <Grid2 xs={12} md={1}>
                <PartLabel
                  idx={idx}
                  component="div"
                  style={{ display: 'block', marginBottom: '5px' }}
                  required={headers?.partTax?.required}>
                  {headers.partTax?.label}
                </PartLabel>
                <NumericTextField
                  decimalScale={2}
                  fixedDecimalScale
                  startAdornment="$"
                  allowNegative={false}
                  name={`${props.field.name}.${idx}.partTax`}
                  permission={props?.permission?.partTax || 'READ'}
                />
              </Grid2>
            )}
            <Grid2 xs={12} md={1}>
              <PartLabel
                idx={idx}
                component="div"
                style={{ display: 'block', marginBottom: '12px' }}
                required={headers?.total?.required}>
                {headers.total?.label}
              </PartLabel>
              <Typography
                sx={{ m: 1 }}
                component="div"
                color="tertiary.main"
                fontWeight="bold"
                variant="body2">
                ${total}
              </Typography>
            </Grid2>
          </Grid2>
        );
      })}
      {(fields.length < maxFields || fields.length === 0) && (
        <Button
          variant="text"
          startIcon={<IconLibrary.Add fontSize="inherit" />}
          onClick={() => {
            append({ ...emptyField });
          }}
          sx={{ mt: 0 }}>
          {formatMessage({
            id: 'SERVICE_JOB_ADD_NEW_PART_BUTTON',
            defaultMessage: 'Add New Part',
          })}
        </Button>
      )}
    </Box>
  );
});

export default observer(Parts);
